import React from 'react';
import { useLang } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Shield, Server, Mail } from 'lucide-react';

export default function Hero() {
  const { t } = useLang();

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="/images/hero-bg.webp"
          alt="Abstract colorful background"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/80" />
      </div>

      {/* Animated orbs */}
      <div className="absolute top-10 left-[5%] w-80 h-80 bg-neon/30 rounded-full blur-[150px] animate-float" />
      <div className="absolute bottom-10 right-[5%] w-64 sm:w-[500px] h-64 sm:h-[500px] bg-pink/20 rounded-full blur-[180px] animate-float-delayed" />
      <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-cyan/20 rounded-full blur-[120px] animate-float-slow" />

      {/* Floating badges */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="hidden lg:flex absolute top-32 right-16 xl:right-32 items-center gap-2 bg-black/60 backdrop-blur-xl border border-neon/30 rounded-2xl px-5 py-3 animate-float"
      >
        <Shield className="w-5 h-5 text-neon" />
        <span className="text-sm font-semibold text-white">DSGVO</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 1.5 }}
        className="hidden lg:flex absolute top-56 right-8 xl:right-20 items-center gap-2 bg-black/60 backdrop-blur-xl border border-pink/30 rounded-2xl px-5 py-3 animate-float-delayed"
      >
        <Server className="w-5 h-5 text-pink" />
        <span className="text-sm font-semibold text-white">Made in DE</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 1.8 }}
        className="hidden lg:flex absolute bottom-40 right-24 xl:right-40 items-center gap-2 bg-black/60 backdrop-blur-xl border border-cyan/30 rounded-2xl px-5 py-3 animate-float-slow"
      >
        <Mail className="w-5 h-5 text-cyan" />
        <span className="text-sm font-semibold text-white">99,9% Uptime</span>
      </motion.div>

      {/* Grid overlay */}
      <div className="absolute inset-0 grid-bg opacity-40" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 lg:py-0">
        <div className="max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-8"
          >
            <img src="/images/zimbra-logo.svg" alt="Zimbra" className="w-12 h-12 sm:w-14 sm:h-14" />
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.25em] uppercase text-neon bg-neon/10 border border-neon/20 rounded-full px-5 py-2">
              <span className="w-2 h-2 bg-neon rounded-full animate-pulse" />
              {t('hero.overline')}
            </p>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="font-heading text-5xl sm:text-7xl lg:text-[6.5rem] xl:text-[7.5rem] font-black tracking-tighter leading-[0.85] text-white mb-8"
          >
            {t('hero.title_lines')?.map((line, i) => (
              <span key={i} className={`block ${i === 1 ? 'text-gradient-animated' : ''}`}>{line}</span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-base sm:text-lg lg:text-xl text-zinc-400 font-light leading-relaxed max-w-2xl mb-12"
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => scrollTo('#pricing')}
              className="group relative inline-flex items-center justify-center gap-2 bg-neon text-black font-bold text-sm uppercase tracking-wide rounded-full px-10 py-5 hover:bg-white hover:shadow-[0_0_40px_rgba(212,255,0,0.6)] transition-all duration-300 glow-neon"
              data-testid="cta-hero-packages"
            >
              <Zap className="w-5 h-5" />
              {t('hero.cta_order')}
            </button>
            <button
              onClick={() => scrollTo('#enterprise')}
              className="group inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/20 text-white font-bold text-sm uppercase tracking-wide rounded-full px-10 py-5 hover:border-neon hover:text-neon transition-all duration-300 backdrop-blur-sm"
              data-testid="cta-hero-enterprise"
            >
              {t('hero.cta_enterprise')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
            </button>
          </motion.div>

          {/* Stats row */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="flex flex-wrap gap-8 lg:gap-16 mt-16 pt-8 border-t border-white/10"
          >
            {[
              { val: '1,69\u20ac', label: 'ab / Monat' },
              { val: '99,9%', label: 'Uptime SLA' },
              { val: '100%', label: 'DSGVO' },
              { val: '\u221e', label: 'Domain-Aliase' },
            ].map((stat, i) => (
              <div key={i} className="count-up" style={{ animationDelay: `${1 + i * 0.15}s` }}>
                <div className="font-heading text-2xl lg:text-3xl font-black text-white">{stat.val}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#050505] to-transparent" />
    </section>
  );
}
